import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  TextField,
  IconButton,
  Modal,
  Button,
  Typography,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import ExpandIcon from "@mui/icons-material/OpenInFull";
import CollapseIcon from "@mui/icons-material/CloseFullscreen";
import InsertEmoticonIcon from "@mui/icons-material/InsertEmoticon";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { Send, AutoAwesome } from "@mui/icons-material";
import ChannelSelector from "./ChannelSelector";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import FilePreview from "./FilePreview";

/** Extrai assunto do último e-mail recebido (emailOriginalTags ou texto) para Re: */
function getDefaultReplySubject(messages = []) {
  const list = Array.isArray(messages) ? messages : [];
  for (let i = 0; i < list.length; i++) {
    const msg = list[i];
    const tags = msg?.emailOriginalTags ?? msg?.emailOriginal ?? "";
    const str = typeof tags === "string" ? tags : "";
    const m = str.match(/\bSubject:\s*([^\n]+)/i);
    if (m && m[1]) return m[1].trim();
  }
  return "";
}

const MessageEmailChannel = ({
  message,
  setMessage,
  channelType,
  openFilePicker,
  handleEmojiPickerOpen,
  handleSendMessage,
  isMobile,
  isExpanded,
  setIsExpanded,
  file,
  files = [],
  handleRemoveFile,
  selectedChannel,
  setSelectedChannel,
  onGenerateSuggestion,
  isGeneratingSuggestion = false,
  hasHistory = false,
  /** E-mail do destinatário da solicitação (pré-preenche "Para") */
  recipientEmail = "",
  /** Mensagens da conversa para extrair assunto em respostas (Re:) */
  messages = [],
}) => {
  const inputRef = useRef(null);
  const inputBorderRadius = 16;
  const canUseAiSuggestion =
    !!onGenerateSuggestion && channelType !== "Xpro" && hasHistory;
  const aiDisabled = isGeneratingSuggestion || !hasHistory;

  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [emailSubject, setEmailSubject] = useState("");

  useEffect(() => {
    const to = (recipientEmail && String(recipientEmail).trim()) || "";
    setEmailTo((prev) => (prev === "" && to ? to : prev || to));
  }, [recipientEmail]);

  useEffect(() => {
    if (!isExpanded) return;
    const sub = getDefaultReplySubject(messages);
    if (sub && !emailSubject) setEmailSubject("Re: " + sub);
  }, [isExpanded, messages, emailSubject]);

  const handleExpand = () => setIsExpanded(true);
  const handleCollapse = () => setIsExpanded(false);

  const onSend = () => {
    const hasContent = message.trim() || files.length > 0 || (file && true);
    const to = emailTo.trim();
    if (hasContent && to && typeof handleSendMessage === "function") {
      handleSendMessage({
        emailTo: to,
        emailCc: emailCc.trim() ? emailCc.trim().split(/\s*[,;]\s*/).filter(Boolean) : undefined,
        emailSubject: emailSubject.trim() || undefined,
      });
      handleCollapse();
    }
  };

  const stripHtml = (html) => {
    const tempDiv = document.createElement("div");
    // Substitui <br> e <div> por quebras de linha
    html = html.replace(/<br\s*\/?>/gi, "\n").replace(/<\/div>/gi, "\n");
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || "";
  };  

  return (
    <Box
        sx={{
        position: "relative", 
        width: "100%",
        display: "flex",
        flexDirection: "column",
        // Mantém alinhado com os demais canais (WhatsApp/SMS/etc).
        paddingBottom: 0,
        }}
    >
      {!isExpanded ? (
        <Box
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          alignItems={isMobile ? "stretch" : "center"}
          gap={isMobile ? 0.5 : 1}
          sx={{ width: "100%", minWidth: 0 }}
        >
            <TextField
                fullWidth
                multiline
                minRows={1}
                maxRows={4}
                variant="outlined"
                placeholder={`Envie uma mensagem via ${channelType}`}
                value={stripHtml(message)}
                onChange={(e) => setMessage(e.target.value)}
                InputProps={{
                    style: {
                      borderRadius: `${inputBorderRadius}px`,
                      fontSize: isMobile ? "0.95rem" : "1rem",
                    },
                    inputRef: inputRef,
                    endAdornment: !isMobile && ( // Exibe botões ao lado apenas no desktop
                    <InputAdornment position="end">
                        {canUseAiSuggestion && (
                          <Tooltip
                            title={
                              !hasHistory
                                ? "Disponível após a conversa ter histórico."
                                : isGeneratingSuggestion
                                ? "Gerando resposta..."
                                : "Sugerir resposta com IA"
                            }
                          >
                            <span>
                              <IconButton
                                onClick={onGenerateSuggestion}
                                disabled={aiDisabled}
                              >
                                <AutoAwesome />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        <IconButton onClick={handleEmojiPickerOpen}>
                        <InsertEmoticonIcon />
                        </IconButton>
                        <IconButton onClick={openFilePicker}>
                        <AttachFileIcon />
                        </IconButton>
                        <IconButton onClick={handleExpand}>
                            <ExpandIcon />
                        </IconButton>
                    </InputAdornment>
                    ),
                }}
                sx={{
                    minWidth: 0,
                    flex: isMobile ? undefined : 1,
                    "& .MuiInputBase-root": {
                    borderRadius: `${inputBorderRadius}px !important`,
                    overflow: "hidden",
                    clipPath: `inset(0 round ${inputBorderRadius}px)`,
                    "&::-webkit-scrollbar": {
                        width: "5px", // Largura da barra de rolagem
                    },
                    "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#b0b0b0", // Cor da barra
                        borderRadius: "10px", // Arredondamento
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                        backgroundColor: "#888888", // Cor da barra ao passar o mouse
                    },
                    "&::-webkit-scrollbar-track": {
                        backgroundColor: "#f1f1f1", // Cor do fundo do track
                    },
                    },
                    "& .MuiOutlinedInput-root": {
                    borderRadius: `${inputBorderRadius}px !important`,
                    overflow: "hidden",
                    clipPath: `inset(0 round ${inputBorderRadius}px)`,
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                    borderRadius: `${inputBorderRadius}px !important`,
                    },
                    "& textarea": {
                    overflowY: "auto",
                    resize: "none",
                    boxSizing: "border-box",
                    borderRadius: "inherit",
                    overflowX: "hidden",
                    padding: "4px 14px",
                    "&::-webkit-scrollbar": {
                        width: "5px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#b0b0b0",
                        borderRadius: "10px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                        backgroundColor: "#888888",
                    },
                    "&::-webkit-scrollbar-track": {
                        backgroundColor: "#f1f1f1",
                    },
                    },
                }}
                />

        {isMobile && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mt: 0.5,
                    px: 0.5,
                    gap: 0.5,
                    width: "100%",
                    minWidth: 0,
                  }}
                >
                  <ChannelSelector
                    selectedChannel={selectedChannel}
                    setSelectedChannel={setSelectedChannel}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.25,
                      flexGrow: 1,
                      justifyContent: "center",
                      minWidth: 0,
                    }}
                  >
                    {canUseAiSuggestion && (
                      <IconButton
                        size="small"
                        onClick={onGenerateSuggestion}
                        disabled={aiDisabled}
                      >
                        <AutoAwesome fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton size="small" onClick={handleEmojiPickerOpen}>
                      <InsertEmoticonIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={openFilePicker}>
                      <AttachFileIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={handleExpand}>
                      <ExpandIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <IconButton
                    onClick={() =>
                      handleSendMessage({ emailTo: emailTo.trim() || recipientEmail })
                    }
                    disabled={
                      (!message.trim() && !file && files.length === 0) ||
                      !(emailTo.trim() || recipientEmail)
                    }
                    sx={{
                      backgroundColor: "#3f51b5",
                      color: "#fff",
                      borderRadius: "50%",
                      p: 1,
                      ml: 0.5,
                      "&:hover": { backgroundColor: "#303f9f" },
                    }}
                  >
                    <Send fontSize="small" />
                  </IconButton>
                </Box>
              )}
        </Box>
      ) : (
        <Modal open={isExpanded} onClose={handleCollapse}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: isMobile ? "90%" : "80%",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 3,
          borderRadius: "8px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Nova Mensagem</Typography>
          <IconButton onClick={handleCollapse} size="small">
            <CollapseIcon />
          </IconButton>
        </Box>

        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="Para"
            placeholder="destinatário@email.com"
            variant="standard"
            value={emailTo}
            onChange={(e) => setEmailTo(e.target.value)}
            InputProps={{ disableUnderline: true }}
            sx={{ mb: 1, borderBottom: "1px solid #ddd" }}
          />
          <TextField
            fullWidth
            label="CC"
            placeholder="opcional, separar por vírgula"
            variant="standard"
            value={emailCc}
            onChange={(e) => setEmailCc(e.target.value)}
            InputProps={{ disableUnderline: true }}
            sx={{ mb: 1, borderBottom: "1px solid #ddd" }}
          />
          <TextField
            fullWidth
            label="Assunto"
            placeholder="Assunto do e-mail"
            variant="standard"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            InputProps={{ disableUnderline: true }}
            sx={{ mb: 2, borderBottom: "1px solid #ddd" }}
          />
        </Box>


        <ReactQuill
          value={message}
          onChange={setMessage}
          theme="snow"
          placeholder="Escreva sua mensagem aqui..."
          style={{
            flex: 1,             // Permite que o editor cresça para ocupar o espaço disponível
            minHeight: "200px",   // Altura mínima
            maxHeight: "300px",   // Altura máxima
            overflowY: "auto",    // Barra de rolagem vertical se o conteúdo exceder a altura
        }}
        />
        
        {(files.length ? files : file ? [file] : []).map((f, index) => (
          <FilePreview
            key={`${f?.name}-${f?.size}-${index}`}
            file={f}
            onRemove={() => handleRemoveFile(index)}
          />
        ))}

        <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
        <IconButton onClick={openFilePicker}>
            <AttachFileIcon />
        </IconButton>
          <Button
            variant="outlined"
            onClick={handleCollapse}
            sx={{ textTransform: "none" }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onSend}
            disabled={(!message.trim() && files.length === 0) || !emailTo.trim()}
            sx={{ textTransform: "none" }}
          >
            Enviar
          </Button>
        </Box>
      </Box>
    </Modal>
      )}
    </Box>
  );
};

export default MessageEmailChannel;
